import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Coins,
  Heart,
  Loader2,
  ArrowRight,
  Trophy,
  Users,
  Gift,
} from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AmbassadorBadge from "@/components/AmbassadorBadge";
import { useAuth } from "@/context/AuthContext";

/**
 * ReferralLanding — public celebration page at `/ref/:handle`.
 *
 * Any Panda X user can share `mypandax.com/ref/<their-handle>` as a
 * referral link. This page fetches the public referrer snapshot, renders
 * a warm invite-card with their avatar + tier badge (if Ambassador), and
 * routes new users to `/register?ref=<handle>` so the backend's existing
 * referral tracking credits both sides on signup.
 *
 * If the referrer handle is invalid, we still let visitors continue but
 * strip the ref param (falling back to a normal signup).
 */
const INVITEE_BONUS_COINS = 50; // matches /auth/register logic

const PERKS = [
  {
    icon: Coins,
    title: `${INVITEE_BONUS_COINS} bonus coins on signup`,
    body: "Automatically credited the moment you finish creating your account.",
  },
  {
    icon: Heart,
    title: "Full access to the community",
    body: "Feed, live streams, communities, reels — everything unlocked for 18+ verified members.",
  },
  {
    icon: Gift,
    title: "Send & receive coin gifts",
    body: "Support your favourite creators on live, or receive tips of your own.",
  },
];

export default function ReferralLanding() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authChecking = user === null;
  const isLoggedIn = !!user && user !== false;

  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setInvalid(false);
    api
      .get(`/ambassador/by-handle/${encodeURIComponent(handle || "")}`)
      .then(({ data }) => {
        if (!cancelled) setReferrer(data);
      })
      .catch(() => {
        if (!cancelled) setInvalid(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [handle]);

  // Own referral link? Show a friendly notice + shareable link instead of
  // letting someone spoof their own signup.
  const isOwnLink = isLoggedIn && referrer && user?.handle === referrer.handle;

  if (loading || authChecking) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 size={16} className="animate-spin" /> Loading your invite…
        </div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center" data-testid="referral-invalid">
        <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-3">
          Invite not found
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">
          That link doesn't ring a bell.
        </h1>
        <p className="text-sm text-zinc-400 max-w-sm leading-relaxed mb-6">
          The invite handle might be misspelled or the account may have been removed. You're
          still welcome — sign up and jump straight in.
        </p>
        <Link
          to="/register"
          data-testid="referral-fallback-register"
          className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-full px-6 py-3 text-sm"
        >
          Create an account <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="referral-landing-page">
      {/* Ambient gradient — rose glow behind the invite card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[600px] bg-[radial-gradient(ellipse_at_top,rgba(225,29,72,0.25),transparent_70%)]"
      />
      <div className="relative max-w-2xl mx-auto px-4 pt-12 pb-20">
        {/* Top branding */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-full bg-rose-600 flex items-center justify-center text-lg">
            🐼
          </div>
          <span className="font-display text-xl tracking-tight">My Panda X</span>
        </Link>

        {/* Invite card */}
        <div className="relative bg-zinc-950/70 border border-white/10 rounded-lg p-6 sm:p-10 text-center overflow-hidden">
          <div className="text-xs uppercase tracking-[0.35em] text-rose-400 mb-4 flex items-center justify-center gap-2">
            <Sparkles size={12} /> You're invited
          </div>

          {/* Referrer avatar */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-rose-500/40 shadow-[0_0_40px_rgba(225,29,72,0.35)]">
                <AvatarImage src={referrer.avatar_url} alt={referrer.display_name} />
                <AvatarFallback className="bg-rose-600 text-white text-2xl">
                  {referrer.display_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {referrer.is_ambassador && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <AmbassadorBadge
                    status="approved"
                    tier={referrer.ambassador_tier}
                    size="sm"
                    linkTo={null}
                  />
                </div>
              )}
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl leading-tight mt-4">
            <span className="text-rose-300">{referrer.display_name}</span>
            <span className="text-white"> wants you in the pack.</span>
          </h1>
          <div className="text-sm text-zinc-500 mt-2" data-testid="referral-handle">
            {referrer.display_name}
          </div>

          {referrer.bio && (
            <p className="text-sm text-zinc-400 max-w-md mx-auto mt-5 leading-relaxed italic">
              “{referrer.bio}”
            </p>
          )}

          {/* Bonus banner */}
          <div className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400/15 to-amber-500/15 border border-amber-400/40 text-amber-200 rounded-full px-4 py-2 text-xs uppercase tracking-widest">
            <Coins size={12} /> {INVITEE_BONUS_COINS} bonus coins waiting for you
          </div>

          {/* Primary CTA */}
          <div className="mt-8">
            {isOwnLink ? (
              <OwnLinkNotice handle={referrer.handle} />
            ) : isLoggedIn ? (
              <button
                type="button"
                onClick={() => navigate("/feed")}
                data-testid="referral-cta-already-in"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white font-semibold rounded-full px-8 py-3 text-sm"
              >
                You're already in — jump to the feed
                <ArrowRight size={14} />
              </button>
            ) : (
              <Link
                to={`/register?ref=${encodeURIComponent(referrer.handle)}`}
                data-testid="referral-cta-claim"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white font-semibold rounded-full px-8 py-3 text-sm"
              >
                Claim your invite <ArrowRight size={14} />
              </Link>
            )}
            {!isLoggedIn && (
              <p className="text-[11px] text-zinc-600 mt-3">
                Already have an account?{" "}
                <Link
                  to={`/login?next=${encodeURIComponent(`/ref/${referrer.handle}`)}`}
                  className="text-rose-300 hover:text-rose-200 underline underline-offset-2"
                  data-testid="referral-cta-login"
                >
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Perks */}
        <section className="mt-10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4 text-center">
            What you unlock
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {PERKS.map((p) => (
              <div
                key={p.title}
                data-testid={`referral-perk-${p.title.split(" ")[0].toLowerCase()}`}
                className="bg-zinc-950/60 border border-white/10 rounded-md p-4 text-left hover:border-rose-500/40 transition-colors"
              >
                <div className="flex items-center gap-2 text-rose-300 mb-2">
                  <p.icon size={16} />
                  <div className="text-sm font-semibold text-white">{p.title}</div>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Referrer stats if approved ambassador */}
        {referrer.is_ambassador && (
          <section className="mt-10 pt-6 border-t border-white/10 text-center" data-testid="referral-ambassador-strip">
            <div className="text-xs uppercase tracking-widest text-amber-400 flex items-center justify-center gap-2 mb-3">
              <Trophy size={12} /> Official Panda Ambassador
            </div>
            <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
              <b className="text-white">{referrer.display_name}</b> has personally invited{" "}
              <b className="text-amber-300">{referrer.referrals_count.toLocaleString()}</b>{" "}
              {referrer.referrals_count === 1 ? "person" : "people"} into the pack.
              Ambassadors are hand-picked creators — you're in good company.
            </p>
            <div className="mt-4">
              <Link
                to="/ambassadors"
                className="inline-flex items-center gap-2 text-xs text-amber-300 hover:text-amber-200"
                data-testid="referral-view-leaderboard"
              >
                <Users size={12} /> See other Ambassadors <ArrowRight size={12} />
              </Link>
            </div>
          </section>
        )}

        {/* Footer */}
        <p className="text-[11px] text-zinc-600 mt-10 text-center flex items-center justify-center gap-1">
          <Heart size={10} className="text-rose-500" /> Every invite grows the pack. Thanks for
          joining us.
        </p>
      </div>
    </div>
  );
}

function OwnLinkNotice({ handle }) {
  const link =
    typeof window !== "undefined"
      ? `${window.location.origin}/ref/${handle}`
      : `/ref/${handle}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      const { toast } = await import("sonner");
      toast.success("Referral link copied.");
    } catch (err) {
      // Non-fatal — user can select the text manually.
      console.debug("clipboard copy failed:", err?.name);
    }
  };
  return (
    <div
      className="bg-black/40 border border-white/10 rounded-md p-4 text-left"
      data-testid="referral-own-link-notice"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
        This is your own referral link
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed mb-3">
        Share it anywhere — every new signup credits <b className="text-white">+100 coins</b> to
        your wallet and climbs your Ambassador tier.
      </p>
      <div className="flex items-center gap-2 bg-black/60 border border-white/10 rounded px-3 py-2 text-xs text-zinc-300 font-mono overflow-hidden">
        <span className="truncate flex-1">{link}</span>
        <button
          type="button"
          onClick={copy}
          data-testid="referral-copy-own-link"
          className="text-rose-300 hover:text-rose-200 uppercase tracking-widest text-[10px]"
        >
          Copy
        </button>
      </div>
    </div>
  );
}
